/* eslint-disable no-use-before-define */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import './FormComponents.scss';
import { injectIntl } from 'react-intl';
import DOMPurify from 'dompurify';

const useStyles = makeStyles(theme => ({
  '@global': {
    '.MuiAutocomplete-option[data-focus="true"]': {
        background: '#F2F4FF'
    }
  },
  root: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: '8px',
    alignContent: 'flex-end',
    '& > * + *': {
      marginTop: theme.spacing(3),
    },
  },
  label: {
    display: 'flex',
    flexWrap: 'wrap',
    alignSelf: 'flex-end',
  },
  helperText: {
    margin: '10px 0 0',
    fontSize: '12px',
    lineHeight: 1.4,
  },
  field: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    alignSelf: 'flex-end',
  },
}));

const MultiSelectDropDownAPI = ({
  options,
  placeholder,
  _handleChange,
  attributeKey,
  defaultValue,
  required,
  showError,
  single,
  readOnly,
  intl,
  supportsHtmlTags,
  filterOptions = createFilterOptions(),
  onFocusHandler,
  formDetails,
}) => {
  const classes = useStyles();
  const inputTextFieldRef = useRef(null);
  const handleChange = (event, val, reason) => {
    if (single) {
      _handleChange(val, attributeKey, reason, event);
    } else {
      _handleChange(val || [], attributeKey);
    }
  };

  const noOption = options ? <>No Option</> : <CircularProgress size={20} />;

  return (
    <div className={classes.root} ref={inputTextFieldRef}>
      <div className={classes.field}>
        {supportsHtmlTags && (
          <div className="support-html">
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(placeholder) }} />
            {required && <span className="mandatory">&nbsp;*</span>}
          </div>
        )}
        <Autocomplete
          autoHighlight
          disabled={readOnly}
          multiple={!single}
          className={classes.root}
          id="size-small-outlined-multi"
          size="small"
          options={options || []}
          getOptionLabel={option => (typeof option === 'string' ? option : option.value)}
          renderOption={option => option.value}
          filterSelectedOptions
          filterOptions={filterOptions}
          getOptionSelected={(option, value) => option.value === value.value}
          onChange={handleChange}
          value={defaultValue}
          noOptionsText={
            <div className="d-flex justify-center">{noOption}</div>
          }
          onFocus={() => {
            onFocusHandler(formDetails);
          }}
          renderInput={params => (
            <TextField
              {...params}
              error={required ? showError : false}
              fullWidth
              label={
                supportsHtmlTags ? (
                  ''
                ) : (
                  <>
                    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(placeholder) }} />
                    {required && <span className="mandatory">&nbsp;*</span>}
                  </>
                )
              }
            />
          )}
        />
      </div>
    </div>
  );
};

MultiSelectDropDownAPI.propTypes = {
  _handleChange: PropTypes.func.isRequired,
  filterOptions: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  attributeKey: PropTypes.string.isRequired,
  defaultValue: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  required: PropTypes.bool.isRequired,
  showError: PropTypes.bool.isRequired,
  single: PropTypes.bool,
  readOnly: PropTypes.bool.isRequired,
  supportsHtmlTags: PropTypes.bool.isRequired,
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired,
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  onFocusHandler: PropTypes.func.isRequired,
  formDetails: PropTypes.shape({}).isRequired,
};

MultiSelectDropDownAPI.defaultProps = {
  single: false,
};

export default injectIntl(MultiSelectDropDownAPI);
